import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// Components
import BoardHeart from 'components/nav/TopNav/BoardHeart';
import CartContainer from 'components/nav/TopNav/CartContainer';
import Messages from 'components/nav/TopNav/Messages';
import Notification from 'components/nav/TopNav/Notification';
import UniversalSearch from 'components/search/UniversalSearch';
import { UserIcon } from '@zola/zola-ui/src/components/SvgIconsV3/User';

// Constants
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import { UniversalTabs } from 'reducers/nav';

// Types
import { AppDispatch } from 'reducers/index';
import { UserContext } from '@zola-helpers/client/dist/es/@types';

// Utils
import { signout } from 'actions/AuthActions';
import { getDefaultLogoutDestination, isLoggedIn } from 'util/authHelpers';
import getAccount from 'components/navV2/NavData/getAccount';
import { getFavoritesLink } from 'util/getFavoritesLink';
import TextUtil from 'util/TextUtil';
import { trackNavigationClicked } from 'util/trackingHelper';
import { NAV_SECTIONS } from 'util/trackNavigationClickedHelpers';
import featureFlags from 'util/featureFlags';
import { getPageName } from '../utils';

// Styles
import {
  PageTitle,
  UtilityBarWrapper,
  UtilityBarRight,
  IconsContainer,
  StyledDropdownV3,
  ProfileImgCircle,
  ProfileCircle,
} from './UtilityBar.styles';

const SearchContainer = React.lazy(() =>
  import(/* webpackChunkName: "search-container" */ 'components/nav/Search/SearchContainer')
);

export type UtilityBarProps = {
  activeLinkId?: '' | PrimaryNavIdentifier;
  activeSecondaryLink: string;
  activeUniversalTab: string;
  hideContentOnScroll: boolean;
  isHomeStoreActive: boolean;
  showSecondaryNav: boolean;
  userContext?: UserContext;
};

const UtilityBar = ({
  activeLinkId,
  activeSecondaryLink,
  activeUniversalTab,
  hideContentOnScroll,
  isHomeStoreActive,
  showSecondaryNav,
  userContext,
}: UtilityBarProps): JSX.Element => {
  const utilityBarRightRef = useRef<HTMLDivElement | null>(null);
  const {
    has_marketplace_account: hasMarketplaceAccount,
    object_id: userId,
    user_role_account_weddings = [],
  } = userContext || {};

  const isWeddingStoreActive = activeUniversalTab === UniversalTabs.WEDDINGS;
  const logoutDestination = getDefaultLogoutDestination(userContext);
  const pageTitle = getPageName(activeLinkId, activeSecondaryLink);
  const showEnvelopeIcon = isLoggedIn(userContext) && hasMarketplaceAccount;
  const { owner_first_name, owner_last_name, profile_image_url: profileImageUrl } =
    user_role_account_weddings[0] || {};
  const firstNameInitial = owner_first_name ? owner_first_name[0] : undefined;
  const lastNameInitial = owner_last_name ? owner_last_name[0] : undefined;
  const userInitials =
    firstNameInitial && lastNameInitial
      ? `${firstNameInitial}${lastNameInitial}`.toUpperCase()
      : null;

  const { children } = getAccount({ userContext, isPostAuthNav3Enabled: true });
  const options = useMemo(
    () => [
      ...(children?.map(({ title, path }) => ({
        label: title as string,
        value: path,
      })) || []),
      {
        label: 'Log out',
        value: logoutDestination,
      },
    ],
    [children, logoutDestination]
  );

  const dispatch: AppDispatch = useDispatch();
  const favoritesLink = useMemo(() => getFavoritesLink(activeLinkId), [activeLinkId]);
  const [isSearchActive, setSearchActive] = useState(false);
  const [isAccountDropdownExpanded, setIsAccountDropdownExpanded] = useState(false);
  const ACCOUNT_DROPDOWN = 'nav-account-dropdown';
  const accountDropdownEl = document
    .getElementsByClassName(ACCOUNT_DROPDOWN)[0]
    ?.querySelector('[data-expanded="false"]');

  useEffect(() => {
    if (accountDropdownEl) {
      const observer = new MutationObserver(() => {
        const isExpanded = accountDropdownEl.getAttribute('data-expanded') === 'true';
        setIsAccountDropdownExpanded(isExpanded);
      });
      observer.observe(accountDropdownEl, { attributes: true });
    }
  }, [accountDropdownEl]);

  const toggleSearchActive = () => {
    setSearchActive(!isSearchActive);
  };

  const optionIndexByPath = (path: string | number | null) => {
    return options.findIndex((option) => option.value === path);
  };

  const handleClick = async (path: string | number | null, isSubMenu?: boolean) => {
    const optionIndex = optionIndexByPath(path);
    const shouldLogOut = path === logoutDestination;

    // Edge case where tabbing through triggers handleClick with a null path
    if (optionIndex < 0) {
      return;
    }

    // Tracking
    const eventData = {
      menu_item: TextUtil.toAllUpperCase(options[optionIndex].label),
      section: isSubMenu ? NAV_SECTIONS.utilitySubNav : NAV_SECTIONS.utility,
      node_level_1: shouldLogOut ? NAV_SECTIONS.utility : 'ACCOUNT',
      row_position: isSubMenu ? optionIndex + 1 : 1,
      column_position: 1,
    };
    trackNavigationClicked(eventData);

    // Log out
    if (shouldLogOut) {
      await dispatch(signout());
    }
    // Reroute
    window.location.assign(path as string);
  };

  const renderAccountCircle = () => {
    if (profileImageUrl) {
      return (
        <ProfileImgCircle
          src={profileImageUrl}
          alt="My account"
          expanded={isAccountDropdownExpanded}
        />
      );
    }
    if (userInitials) {
      return <ProfileCircle expanded={isAccountDropdownExpanded}>{userInitials}</ProfileCircle>;
    }
    return (
      <ProfileCircle expanded={isAccountDropdownExpanded}>
        <UserIcon height={20} width={20} />
      </ProfileCircle>
    );
  };

  return (
    <UtilityBarWrapper
      hideContentOnScroll={hideContentOnScroll}
      showSecondaryNav={showSecondaryNav}
    >
      <PageTitle hideContentOnScroll={hideContentOnScroll} showSecondaryNav={showSecondaryNav}>
        {pageTitle}
      </PageTitle>
      <Suspense fallback={<div>Loading</div>}>
        <UtilityBarRight ref={utilityBarRightRef}>
          {featureFlags.get('universalSearch') ? (
            <UniversalSearch activeLinkId={activeLinkId} />
          ) : (
            <SearchContainer
              hideToggle={!isWeddingStoreActive}
              isActive={isSearchActive}
              isPostAuthNav3Enabled
              isWeddingStoreActive={isWeddingStoreActive}
              toggleSearchActive={toggleSearchActive}
            />
          )}
          <IconsContainer>
            {showEnvelopeIcon ? <Messages isPostAuthNav3Enabled userId={userId as string} /> : null}
            {isWeddingStoreActive ? (
              <BoardHeart isPostAuthNav3Enabled link={favoritesLink} userContext={userContext} />
            ) : null}
            {!isHomeStoreActive && isLoggedIn(userContext) ? (
              <Notification isPostAuthNav3Enabled userId={userId as string} />
            ) : null}
            <CartContainer isPostAuthNav3Enabled />
          </IconsContainer>
          <StyledDropdownV3
            boxAlign="right"
            boxWidth="190px"
            className={ACCOUNT_DROPDOWN}
            isHoverTrigger
            onClick={() => handleClick('/account/settings/info', false)}
            onSelect={(path) => handleClick(path, true)}
            options={options}
            trigger={renderAccountCircle()}
          />
        </UtilityBarRight>
      </Suspense>
    </UtilityBarWrapper>
  );
};

export default UtilityBar;
